import { useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { 
    getZoneStatus, 
    claimZoneStatus, 
    updateZoneStatus, 
    updateZonePayload,
    clearZoneError,
    ZONE_IDS,
    type ZoneId 
} from '../store/slices/zonesSlice';
import type { ZoneStatusData, ZonePayloadData } from '../services/apiService';

// Hook for managing individual zone status
export const useZoneStatus = (zoneId: string, visitorId?: string) => {
    const dispatch = useAppDispatch();
    const { zones, loading, error, claimingZone, lastChecked } = useAppSelector(state => state.zones);
    
    const zoneStatus = zones[zoneId];
    const isLoading = loading[zoneId] || false;
    const zoneError = error[zoneId];
    const isClaiming = claimingZone === zoneId;
    const lastCheck = lastChecked[zoneId];

    // Fetch zone status
    const fetchZoneStatus = useCallback(() => {
        dispatch(getZoneStatus({ zoneId, visitorId }));
    }, [dispatch, zoneId, visitorId]);

    // Claim zone
    const claimZone = useCallback(async (visitorId?: string) => {
        console.log(`🎯 Claiming zone: ${zoneId}`, { 
            hasVisitorId: !!visitorId,
            visitorIdPreview: visitorId ? `${visitorId.substring(0, 8)}...` : 'none'
        });
        const result = await dispatch(claimZoneStatus({ zoneId, visitorId }));
        return result;
    }, [dispatch, zoneId]);

    // Update zone status
    const updateStatus = useCallback(async (statusData: ZoneStatusData) => {
        console.log(`🔄 Updating status for zone: ${zoneId}`, statusData);
        const result = await dispatch(updateZoneStatus({ zoneId, statusData, visitorId }));
        return result;
    }, [dispatch, zoneId, visitorId]);

    // Update zone payload
    const updatePayload = useCallback(async (payloadData: ZonePayloadData) => {
        console.log(`📦 Updating payload for zone: ${zoneId}`, payloadData);
        const result = await dispatch(updateZonePayload({ zoneId, payloadData, visitorId }));
        return result;
    }, [dispatch, zoneId, visitorId]);

    // Clear zone error
    const clearError = useCallback(() => {
        if (zoneError) {
            dispatch(clearZoneError(zoneId));
        }
    }, [dispatch, zoneId, zoneError]);

    // Auto-fetch on mount if no data exists or data is stale (older than 30 seconds)
    useEffect(() => {
        const shouldFetch = !zoneStatus || 
                          !lastCheck || 
                          (Date.now() - lastCheck) > 30000; // 30 seconds
        
        if (shouldFetch && !isLoading) {
            fetchZoneStatus();
        }
    }, [zoneId, zoneStatus, lastCheck, isLoading, fetchZoneStatus]);

    return {
        // Zone data
        zoneStatus,
        isLoading,
        error: zoneError,
        isClaiming,
        lastCheck,
        
        // Zone status helpers
        isAvailable: zoneStatus?.status === 'available',
        isProcessing: zoneStatus?.status === 'processing',
        isError: zoneStatus?.status === 'error',
        isClaimedByMe: zoneStatus?.claimedBy !== undefined, // TODO: Add visitor ID comparison
        
        // Actions
        fetchZoneStatus,
        claimZone,
        updateStatus,
        updatePayload,
        clearError,
    };
};

// Hook for managing multiple zones (useful for admin views)
export const useMultipleZones = (zoneIds: string[]) => {
    const dispatch = useAppDispatch();
    const { zones, loading, error } = useAppSelector(state => state.zones);
    
    const fetchAllZones = useCallback(() => {
        console.log(`🔍 Fetching status for multiple zones:`, zoneIds);
        zoneIds.forEach(zoneId => {
            dispatch(getZoneStatus({ zoneId }));
        });
    }, [dispatch, zoneIds]);

    const zonesData = zoneIds.map(zoneId => ({
        zoneId,
        status: zones[zoneId],
        isLoading: loading[zoneId] || false,
        error: error[zoneId],
    }));

    const isAnyLoading = zoneIds.some(zoneId => loading[zoneId]);
    const hasAnyError = zoneIds.some(zoneId => error[zoneId]);

    return {
        zonesData,
        isAnyLoading,
        hasAnyError,
        fetchAllZones,
    };
};

// Hook specifically for Orang Laut exhibit
export const useOrangLautZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.ORANG_LAUT, visitorId);
};

// Hook specifically for Market Traders exhibit
export const useMarketTradersZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.MARKET_TRADERS, visitorId);
};

// Hook specifically for Peranakan Design exhibit
export const usePeranakanDesignZone = (visitorId?: string) => {
    return useZoneStatus(ZONE_IDS.PERANAKAN_DESIGN, visitorId);
}; 